import AlertPopup from "components/alert-popup/AlertPopup";
import Button from "core/buttons/button/Button";
import ConsultantCorporateInfoForm from "components/form/consultant/ConsultantCorporateInfoForm";
import ConsultantPersonalInfoForm from "components/form/consultant/ConsultantPersonalInfoForm";
import DoyoubuzzForm from "components/form/consultant/DoyoubuzzForm";
import InfoContext from "pages/InfoContext";
import PageContext from "pages/PageContext";
import ResidencePermitForm from "components/form/residence-permit/ResidencePermitForm";
import PastDateWarningModal from "components/modal/PastDateWarningModal";
import ValidationModal from "components/modal/ValidationModal";
import { FIELD_REQUIRED } from "constants/popup-codes";
import {
  FILTERS,
  QUERY_STATUS,
  POPUP_STATUS,
  VISA,
  STAFF_PROFILES,
  FILTERS_FOR_NAMES,
} from "constants/constants";
import { FormattedMessage } from "react-intl";
import { formatCreatedData } from "utils/consultantHelper";
import { useBranchesContext } from "api/settings/branches/BranchesProvider";
import { useConsultantContext } from "api/directory/consultant/ConsultantProvider";
import { useCurrencyContext } from "api/settings/currency/CurrencyProvider";
import { useDybContext } from "api/external-apis/dyb/DybProvider";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useFileContext } from "api/files/FilesProvider";
import { useForm } from "react-hook-form";
import { useJobContext } from "api/settings/job/JobProvider";
import { useNavigate } from "react-router-dom";
import { useReasonLeaveContext } from "api/settings/reason-leave/ReasonLeaveProvider";
import { useRecruitmentMethodContext } from "api/settings/recruitment-method/RecruitmentMethodProviders";
import { useSyntecContext } from "api/settings/syntec/SyntecProvider";
import { useUserContext } from "api/user/UserProvider";
import { useProfileUploadSetup, useWindowSize } from "utils/customHooks";
import { useGeneralContext } from "api/directory/general/GeneralProvider";
import {
  CONSULTANT_FORM_DEFAULT_VALUES,
  CONSULTANT_FORM_LABELS,
  CONSULTANT_FORM_PLACEHOLDERS,
} from "constants/forms";
import { useNationalitiesContext } from "api/settings/nationalities/NationalitiesProvider";
import { useContractualStatusContext } from "api/settings/contractual-status/ContractualStatusProvider";
import colors from "assets/styles/mixins/colors.scss";
import { ROLE_MGR } from "access-roles/roles";
import moment from "moment";

const CreateConsultant = () => {
  const {
    control,
    formState: { errors },
    handleSubmit,
    register,
    unregister,
    setValue,
    watch,
    clearErrors,
    reset,
    setError,
  } = useForm({
    defaultValues: CONSULTANT_FORM_DEFAULT_VALUES,
  });
  const { fetchBranches, branches } = useBranchesContext();
  const {
    consultantCreate,
    createConsultantStatus,
    createdConsultant,
    apiErrorResponse,
    editConsultantStatus,
  } = useConsultantContext();
  const { getNamesByFilters, namesByFilters } = useGeneralContext();
  const {
    currencyListByBranchGetter,
    currencyListByBranch,
    setCurrencyListByBranch,
  } = useCurrencyContext();
  const { jobList, jobListGetter } = useJobContext();
  const { lang, userScope, setApiErrorResponse } = useUserContext();
  const { reasonLeaveList, reasonLeaveListGetter } = useReasonLeaveContext();
  const { recruitmentMethodList, recruitmentMethodListGetter } =
    useRecruitmentMethodContext();
  const { currencyList, currencyListGetter } = useCurrencyContext();
  const { searchUsersByName, usersByName } = useDybContext();
  const { syntecCoefList, syntecCoefListGetter } = useSyntecContext();
  const { createdFile, setCreatedFile, createFile, uploadProfilePicture } =
    useFileContext();
  const navigate = useNavigate();
  const { nationalitiesList, nationalitiesListGetter } =
    useNationalitiesContext();
  const { contractualStatusList, contractualStatusListGetter } =
    useContractualStatusContext();
  const { connectedUserFormData } = useContext(PageContext);

  const [isOpen, setIsOpen] = useState(false);
  const [showSection, setShowSection] = useState(false);
  const [isValidated, setIsValidated] = useState(false);
  const [cellHighlight, setCellHighlight] = useState(false);
  const [isPastDateWarningOpen, setIsPastDateWarningOpen] = useState(false);
  const pendingFormDataRef = useRef(null);
  // Synchronous lock: blocks re-entrant submits before React re-renders the disabled button
  const isSubmittingRef = useRef(false);

  useEffect(() => {
    const fieldsMapping = {
      branch_id: "branch",
      manager_id: "manager",
      gp_id: "gp",
    };
    Object.entries(fieldsMapping).forEach(([formField, dataKey]) => {
      if (
        connectedUserFormData?.[dataKey]?.length > 0 &&
        connectedUserFormData["role"].includes(ROLE_MGR)
      ) {
        setValue(formField, connectedUserFormData[dataKey]);
      }
    });
  }, [connectedUserFormData, setValue]);

  const flattedsyntecCoefList = syntecCoefList?.flatMap((e) =>
    e.coef_position_syntec.map((elm) => ({ ...elm }))
  );

  const { picture, tempUserId } = useProfileUploadSetup();

  useEffect(() => {
    nationalitiesListGetter();
    contractualStatusListGetter();
  }, [lang]);

  useEffect(() => {
    isValidated && errors && setCellHighlight(true);
  }, [errors, isValidated]);

  const size = useWindowSize();
  useEffect(() => {
    recruitmentMethodListGetter();
    fetchBranches();
    jobListGetter(FILTERS.CSLT);
    reasonLeaveListGetter();
    syntecCoefListGetter();
    currencyListGetter();
  }, [
    currencyListGetter,
    syntecCoefListGetter,
    reasonLeaveListGetter,
    jobListGetter,
    fetchBranches,
    recruitmentMethodListGetter,
  ]);

  const onSubmit = async (info) => {
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    try {
      await consultantCreate({
        ...info,
        end_date: info.end_date ? info.end_date : null,
      });
    } finally {
      isSubmittingRef.current = false;
    }
  };

  const getOptions = (search) => {
    getNamesByFilters({
      profile: [...STAFF_PROFILES, "cslt", "str"],
      types: [FILTERS_FOR_NAMES.PERSON],
      search,
    });
  };

  const getPersonsByName = (search) => {
    searchUsersByName({ search });
  };

  const [roles, setRoles] = useState([]);
  const [code, setCode] = useState();
  const [status, setStatus] = useState();

  const proceedWithCreation = (result) => {
    onSubmit(result);
    setIsOpen(true);
  };

  const formatData = (data) => {
    const result = formatCreatedData(
      { ...data, role: roles },
      { contractualStatusList, flattedsyntecCoefList }
    );

    if (
      data.start_date &&
      moment(data.start_date).isBefore(moment().startOf("day"))
    ) {
      pendingFormDataRef.current = result;
      setIsPastDateWarningOpen(true);
      return;
    }

    proceedWithCreation(result);
  };

  const handlePastDateContinue = () => {
    setIsPastDateWarningOpen(false);
    if (pendingFormDataRef.current) {
      proceedWithCreation(pendingFormDataRef.current);
      pendingFormDataRef.current = null;
    }
  };

  const resetForm = () => {
    setRoles([]);
    reset(CONSULTANT_FORM_DEFAULT_VALUES);
    window.scrollTo(0, 0);
  };

  const onError = ({ errorMessage }) => {
    setStatus(POPUP_STATUS.ERROR);
    setCode(errorMessage || FIELD_REQUIRED);
  };

  useEffect(() => {
    setApiErrorResponse(null);
  }, []);

  useEffect(() => {
    if (apiErrorResponse) {
      Object.entries(apiErrorResponse).forEach(([field, message]) => {
        setError(field, { type: "manual", message });
      });
    }
  }, [apiErrorResponse, setError]);

  const value = useMemo(
    () => ({
      branches: branches,
      currencyList: currencyList,
      davFunctions: jobList,
      getOptions,
      getPersonsByName: getPersonsByName,
      isMobile: size.width < 600,
      lang: lang,
      leavingReasons: reasonLeaveList,
      personsByProfileList: namesByFilters,
      recruitmentMethodList: recruitmentMethodList,
      syntecCoef: flattedsyntecCoefList,
      usersByName: usersByName,
      nationalities: nationalitiesList,
      contractualStatusList,
      createFile,
      uploadProfilePicture,
      createdFile,
      setCreatedFile,
      setCode,
      setStatus,
      apiErrorResponse,
      cellHighlight,
      picture,
      tempUserId,
      currencyListByBranchGetter,
      currencyListByBranch,
      setCurrencyListByBranch,
      editConsultantStatus,
    }),
    [
      branches,
      currencyList,
      jobList,
      lang,
      namesByFilters,
      reasonLeaveList,
      recruitmentMethodList,
      flattedsyntecCoefList,
      usersByName,
      nationalitiesList,
      contractualStatusList,
      createFile,
      uploadProfilePicture,
      createdFile,
      setCreatedFile,
      picture,
      apiErrorResponse,
      cellHighlight,
      tempUserId,
      currencyListByBranchGetter,
      currencyListByBranch,
      setCurrencyListByBranch,
      editConsultantStatus,
    ]
  );
  return (
    <>
      <PastDateWarningModal
        open={isPastDateWarningOpen}
        onContinue={handlePastDateContinue}
        onClose={() => setIsPastDateWarningOpen(false)}
        messageId="modal_past_contract_date_warning_msg"
      />
      <ValidationModal
        open={isOpen}
        onClose={() => {
          setIsOpen(false);
          return (
            createConsultantStatus === QUERY_STATUS.SUCCESS &&
            navigate(`/${userScope}/consultant/${createdConsultant?.slug}`)
          );
        }}
        status={createConsultantStatus}
        messages={{
          pendingTitleId: "modal_validation_create_file",
          pendingMessageId: "modal_validation_file_pending_msg",
          successTitleId: "modal_validation_create_file",
          successMessageId: "modal_validation_file_success_msg",
          errorTitleId: "modal_validation_create_file",
          errorMessageId: "modal_validation_field_errors_msg",
          errorMessageDefault:
            "Certains champs comportent des erreurs.{br}Veuillez cliquer sur les <nowrap>icônes{errorIcon}pour</nowrap> obtenir plus de détails et corriger les informations nécessaires.",
        }}
      />
      <InfoContext.Provider value={value}>
        <AlertPopup
          code={code}
          setCode={setCode}
          setStatus={setStatus}
          status={status}
        />
        <form
          onSubmit={(e) => {
              clearErrors();
              handleSubmit(formatData, onError)(e);
            }}
          style={{ paddingTop: "2rem" }}
        >
          <DoyoubuzzForm
            control={control}
            errors={errors}
            register={register}
            roles={roles}
            setRoles={setRoles}
            setValue={(field, value) => {
              setValue(field, value);
              clearErrors(field);
            }}
            watch={watch}
            cellHighlight={cellHighlight}
            apiErrorResponse={apiErrorResponse}
          />
          <ConsultantPersonalInfoForm
            setValue={(field, value) => {
              setValue(field, value);
              clearErrors(field);
            }}
            watch={watch}
            control={control}
            register={register}
            errors={errors}
            unregister={unregister}
            cellHighlight={cellHighlight}
            apiErrorResponse={apiErrorResponse}
          />
          <br />
          <ResidencePermitForm
            showSection={showSection}
            setShowSection={setShowSection}
            action={VISA.ADD}
            setValue={(field, value) => {
              setValue(field, value);
              clearErrors(field);
            }}
            watch={watch}
            control={control}
            register={register}
            labels={CONSULTANT_FORM_LABELS}
            placeholders={CONSULTANT_FORM_PLACEHOLDERS}
          />
          <ConsultantCorporateInfoForm
            setValue={(field, value) => {
              setValue(field, value);
              clearErrors(field);
            }}
            watch={watch}
            control={control}
            register={register}
            errors={errors}
            unregister={unregister}
            clearErrors={clearErrors}
            cellHighlight={cellHighlight}
            apiErrorResponse={apiErrorResponse}
          />
          <div className="form-buttons">
            <Button
              className="btn-standard btn-outline"
              onClick={resetForm}
              type="button"
              id="button-form-reset"
            >
              <FormattedMessage id="cancel" defaultMessage="Annuler" />
            </Button>
            <Button
              color="btn-medium gray-700"
              type="submit"
              id="button-form-validation"
              onClick={() => setIsValidated(true)}
              disabled={createConsultantStatus === QUERY_STATUS.LOADING}
              backgroundColor={
                createConsultantStatus === QUERY_STATUS.LOADING &&
                colors.disabledGrey
              }
            >
              <FormattedMessage
                id="button_form_validation"
                defaultMessage="Valider la création"
              />
            </Button>
          </div>
        </form>
      </InfoContext.Provider>
    </>
  );
};

export default CreateConsultant;
